/**
 * Séries de points à aborder d'un tuteur (migration 0247) — gérées en direct
 * sous RLS propriétaire (calque `live_registrations` : pas d'edge fn). Tables
 * postérieures aux types générés → client générique (calque `rendezvous.ts`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  defaultSeriesInputs,
  parseSeriesPoints,
  type VisioSeries,
  type VisioSeriesInput,
} from '@jeraisonne/db/visio';
import { supabase } from '@/lib/supabase';
import { fetchVisioRoomId } from '@/lib/visio/room-lookup';

const db = supabase as unknown as SupabaseClient;

const seriesKey = (ownerId: string | undefined) => ['visio-series', ownerId] as const;

interface SeriesRow {
  id: string;
  title: string;
  points: unknown;
  format_md: string | null;
}

function toRow(input: VisioSeriesInput) {
  return { title: input.title, points: input.points, format_md: input.formatMd ?? null };
}

/** Les séries DU tuteur connecté (la RLS ne renvoie de toute façon que les siennes), plus anciennes d'abord. */
export function useMyVisioSeries(ownerId: string | undefined) {
  return useQuery({
    queryKey: seriesKey(ownerId),
    enabled: !!ownerId,
    queryFn: async (): Promise<VisioSeries[]> => {
      const { data, error } = await db
        .from('visio_instruction_series')
        .select('id, title, points, format_md')
        .eq('owner_id', ownerId!)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return ((data ?? []) as SeriesRow[]).map((row) => ({
        id: row.id,
        title: row.title,
        points: parseSeriesPoints(row.points),
        formatMd: row.format_md,
      }));
    },
  });
}

/** Crée (sans `id`) ou met à jour (avec `id`) une série. */
export function useSaveVisioSeries(ownerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; series: VisioSeriesInput }) => {
      if (!ownerId) throw new Error('missing ownerId');
      const row = toRow(input.series);
      const { error } = input.id
        ? await db.from('visio_instruction_series').update(row).eq('id', input.id)
        : await db.from('visio_instruction_series').insert({ owner_id: ownerId, ...row });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: seriesKey(ownerId) }),
  });
}

/** Supprime une série — les résumés/checklists qui y renvoient passent à NULL (le titre du résumé reste). */
export function useDeleteVisioSeries(ownerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (seriesId: string) => {
      const { error } = await db.from('visio_instruction_series').delete().eq('id', seriesId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: seriesKey(ownerId) }),
  });
}

/** Ajoute les séries de départ (ex-prompts par type de rendez-vous) à la liste du tuteur. */
export function useSeedDefaultVisioSeries(ownerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!ownerId) throw new Error('missing ownerId');
      const rows = defaultSeriesInputs().map((series) => ({ owner_id: ownerId, ...toRow(series) }));
      const { error } = await db.from('visio_instruction_series').insert(rows);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: seriesKey(ownerId) }),
  });
}

/**
 * Série proposée PAR DÉFAUT pour ce rendez-vous : celle que le tuteur a choisie
 * PENDANT la visio (`visio_room_checklists`), sinon celle choisie à la CRÉATION
 * du rendez-vous (`rendezvous_series_choices`, migration 0248). `null` si aucune.
 */
export function usePreferredSeriesId(reservationId: string | undefined) {
  return useQuery({
    queryKey: ['visio-preferred-series', reservationId],
    enabled: !!reservationId,
    queryFn: async (): Promise<string | null> => {
      // Une salle par CRÉNEAU (0255) : on la retrouve par le créneau de la réservation.
      const roomId = await fetchVisioRoomId(db, reservationId!);
      if (roomId) {
        const { data, error } = await db
          .from('visio_room_checklists')
          .select('series_id')
          .eq('room_id', roomId)
          .maybeSingle();
        if (error) throw new Error(error.message);
        const duringVisio = (data as { series_id: string | null } | null)?.series_id;
        if (duringVisio) return duringVisio;
      }
      const { data: planned, error: plannedError } = await db
        .from('rendezvous_series_choices')
        .select('series_id')
        .eq('reservation_id', reservationId!)
        .maybeSingle();
      if (plannedError) throw new Error(plannedError.message);
      return (planned as { series_id: string | null } | null)?.series_id ?? null;
    },
  });
}
